import { appstr } from "@coreapp/utils/appstr";
import { ListItemOption } from "@theorgchartdev/storybook";
import {
  ChartSettingsSectionRoutes,
  getChartSettingsTestPrefix
} from "@/providers/NavigationProvider";

export const FIELDS_FORMULAS_TEST_PREFIX = getChartSettingsTestPrefix(
  ChartSettingsSectionRoutes.FieldsAndFormulas
);

type PartialAggregatedField = {
  name: string;
};

type PartialStaticFormula = {
  name: string;
};

type PartialScriptFormula = {
  formulaName: string;
};

type PartialChartMetadata = {
  headers?: string[];
  aggregatedFields?: PartialAggregatedField[];
  formulas?: PartialStaticFormula[];
  scriptFormulas?: PartialScriptFormula[];
};

type FilterPredicate = (
  key: "headers" | "aggregatedFields" | "formulas" | "scriptFormulas",
  idx: number,
  value: string | PartialAggregatedField | PartialStaticFormula | PartialScriptFormula
) => boolean;

const DefaultFilterPredicate: FilterPredicate = () => true;

export const GetAllFieldNames = (
  chartMetadata: PartialChartMetadata,
  filterPredicate = DefaultFilterPredicate
) => {
  const allNonHeaderFieldNames: string[] = [];
  const filteredFieldNames: string[] = [];

  (chartMetadata.aggregatedFields ?? []).forEach((field, idx) => {
    allNonHeaderFieldNames.push(field.name);
    if (!filterPredicate("aggregatedFields", idx, field)) {
      return;
    }

    filteredFieldNames.push(field.name);
  });

  (chartMetadata.formulas ?? []).forEach((formula, idx) => {
    allNonHeaderFieldNames.push(formula.name);
    if (!filterPredicate("formulas", idx, formula)) {
      return;
    }

    filteredFieldNames.push(formula.name);
  });

  (chartMetadata.scriptFormulas ?? []).forEach((formula, idx) => {
    allNonHeaderFieldNames.push(formula.formulaName);
    if (!filterPredicate("scriptFormulas", idx, formula)) {
      return;
    }

    filteredFieldNames.push(formula.formulaName);
  });

  (chartMetadata.headers ?? []).forEach((header, idx) => {
    if (allNonHeaderFieldNames.includes(header)) {
      return;
    }

    if (!filterPredicate("headers", idx, header)) {
      return;
    }

    filteredFieldNames.push(header);
  });

  return filteredFieldNames;
};

export const getFieldClassTypeFromGroup = (groupValue: string): string | null => {
  if (groupValue === "employeeFields") {
    return "employee";
  } else if (groupValue === "positionFields") {
    return "position";
  }
  return null;
};

export const addOrganizedFieldOptionSuffix = (
  fieldName: string,
  fieldType: "position" | "employee"
): string => {
  if (fieldType === "position") {
    return `${fieldName}--position`;
  } else if (fieldType === "employee") {
    return `${fieldName}--employee`;
  }
  return fieldName;
};

export const removeOrganizedFieldOptionSuffix = (fieldValue: string): string => {
  if (fieldValue.endsWith("--position")) {
    return fieldValue.slice(0, -10); // Remove "--position"
  } else if (fieldValue.endsWith("--employee")) {
    return fieldValue.slice(0, -10); // Remove "--employee"
  }
  return fieldValue;
};

export const getOrganizedFieldOptionValue = (
  fieldName: string,
  fieldClassType: string | null
): string => {
  if (!fieldName || !fieldClassType) return fieldName;

  if (fieldClassType === "position") {
    return addOrganizedFieldOptionSuffix(fieldName, "position");
  } else if (fieldClassType === "employee") {
    return addOrganizedFieldOptionSuffix(fieldName, "employee");
  }
  return fieldName;
};

export const buildOrganizedFieldOptions = (
  availableFields: string[],
  positionFields: string[],
  holderFields: string[]
): ListItemOption[] => {
  // dedupe
  const uniquePositionFields = [...new Set(positionFields)];
  const uniqueHolderFields = [...new Set(holderFields)];
  const uniqueAvailableFields = [...new Set(availableFields)];

  const employeeFieldOptions = uniqueHolderFields.map((field: string) => ({
    label: field,
    value: addOrganizedFieldOptionSuffix(field, "employee")
  }));

  const positionFieldOptions = uniquePositionFields.map((field: string) => ({
    label: field,
    value: addOrganizedFieldOptionSuffix(field, "position")
  }));

  const categorizedFields = [...uniquePositionFields, ...uniqueHolderFields];
  const uncategorizedFields = uniqueAvailableFields.filter(
    field => !categorizedFields.includes(field)
  );
  uncategorizedFields.forEach(field => {
    employeeFieldOptions.push({
      label: field,
      value: addOrganizedFieldOptionSuffix(field, "employee")
    });
  });

  return [
    {
      label: appstr("Employee fields"),
      value: "employeeFields",
      keepOpenOnClick: true,
      options: employeeFieldOptions
    },
    {
      label: appstr("Position fields"),
      value: "positionFields",
      keepOpenOnClick: true,
      options: positionFieldOptions
    }
  ];
};
